<template>
	<b-dropdown
		class="message-type-filter"
		menu-class="message-type-filter-menu"
		toggle-class="message-type-filter-btn"
		:variant="variant"
		size="sm"
		right
		no-caret
		ref="dropdown"
		:dropup="dropup"
		:title="filterTitle"
		@show="fitMenu()"
		@shown="$refs.dropdown.updatePopper()"
	>
		<template #button-content>
			<b-icon :icon="isFiltered ? 'gear-fill' : 'gear'"></b-icon>
		</template>
		<b-dropdown-form @submit.stop.prevent>
			<div class="d-flex align-items-center mb-2">
				<span class="mr-auto small text-uppercase text-muted">Message types</span>
				<b-button variant="link" size="sm" class="p-0 ml-2 message-type-filter-action" @click="showAllTypes()">All</b-button>
				<b-button variant="link" size="sm" class="p-0 ml-2 message-type-filter-action" @click="showNoTypes()">None</b-button>
				<b-button variant="link" size="sm" class="p-0 ml-2 message-type-filter-action" @click="resetTypes()">Reset</b-button>
			</div>
			<div v-if="loading > 0" class="text-center py-2"><b-spinner small></b-spinner></div>
			<div class="message-type-filter-groups">
				<div v-for="group in groups" :key="group.key" class="message-type-filter-group">
					<div v-if="groups.length > 1" class="message-type-filter-group-title">{{group.title}}</div>
					<div v-for="t in group.types" :key="t.event_type" class="message-type-filter-row">
						<b-form-checkbox
							class="flex-fill"
							:checked="!hidden.includes(t.event_type)"
							@change="setTypeVisible(t.event_type, $event)"
						>{{typeLabel(t)}}</b-form-checkbox>
						<b-button
							v-if="showTests && t.event_type != 'gift'"
							variant="link"
							size="sm"
							class="p-0 ml-3 message-type-filter-test"
							:disabled="t.status == 'disabled'"
							:title="t.status == 'disabled' ? 'Alert type disabled' : 'Play a test alert'"
							@click="$emit('test', { type: t.event_type })"
						>test</b-button>
					</div>
				</div>
			</div>
			<!-- Gift alerts are one type but many value thresholds, so they get their own test list. -->
			<div v-if="showTests" class="message-type-filter-gifts">
				<div class="message-type-filter-group-title">Test gift alerts</div>
				<div v-for="t in giftTests" :key="t.label" class="message-type-filter-row">
					<span class="flex-fill">{{t.label}}</span>
					<b-button variant="link" size="sm" class="p-0 ml-3 message-type-filter-test" title="Play a test alert" @click="$emit('test', { type: 'gift', data: t.data })">test</b-button>
				</div>
			</div>
		</b-dropdown-form>
		<b-dropdown-divider></b-dropdown-divider>
		<b-dropdown-item-button variant="danger" @click="$emit('clear')">
			<b-icon icon="trash" class="mr-2"></b-icon>{{mode == 'alerts' ? 'Clear alert log' : 'Clear chat log'}}
		</b-dropdown-item-button>
	</b-dropdown>
</template>

<script>
import util from '@/util';
import { getDefaultHiddenMessageTypes, getUnfilterableMessageTypes, saveHiddenMessageTypes } from '@/logMessageFilter';

// The event media page labels these from the point of view of configuring media for them;
// in a type filter they just need a plain name.
const TYPE_LABELS = {
	gift: 'Gift / donation',
};

export default {
	name: 'MessageTypeFilter',
	props: {
		// 'chat' offers both the message and the alert event types, 'alerts' only the alert ones.
		mode: { type: String, default: 'chat' },
		// Event types that are currently filtered out of the log.
		value: { type: Array, default: () => [] },
		// Adds a "test" link to every type, merging the alert log's old Test menu into the filter.
		showTests: { type: Boolean, default: false },
		// Gift value thresholds, for the separate gift test list (only used with showTests).
		giftThresholds: { type: Array, default: () => [] },
		currency: { type: String, default: null },
		variant: { type: String, default: 'link' },
		dropup: { type: Boolean, default: false },
	},
	data() { return {
		loading: 0,
		eventTypes: [],
	}},
	computed: {
		hidden() { return this.value || []; },
		// Types this log's filter has no say over, so they are left out of the menu entirely.
		unfilterable() { return [ 'alert', 'default', ...getUnfilterableMessageTypes(this.mode) ]; },
		// Same split as the event media page ('alert' and 'default' are fallback media
		// settings rather than message types of their own).
		alertTypes() { return this.eventTypes.filter(t => t.event_alert_type == 'alert' && !this.unfilterable.includes(t.event_type)); },
		messageTypes() { return this.eventTypes.filter(t => t.event_alert_type == 'event' && !this.unfilterable.includes(t.event_type)); },
		groups() {
			if(this.mode == 'alerts')
				return [ { key: 'alerts', title: 'Alerts', types: this.alertTypes } ];
			return [
				{ key: 'messages', title: 'Messages', types: this.messageTypes },
				{ key: 'alerts', title: 'Alerts', types: this.alertTypes },
			];
		},
		allTypes() { return this.groups.flatMap(g => g.types.map(t => t.event_type)); },
		// One entry per threshold band, each testing a gift worth just that band's minimum.
		giftTests() {
			const thresholds = this.giftThresholds || [];
			return [ { amount: 0 }, ...thresholds ].map((t, idx) => ({
				label: `Gift from ${util.generateValueString(t.amount, this.currency)}`,
				data: {
					// The $0 entry has no threshold of its own, it is everything below the first one.
					...(idx > 0 ? { threshold: t.amount } : {}),
					below: idx < thresholds.length ? thresholds[idx].amount : null,
					currency: this.currency,
				},
			}));
		},
		// The button only advertises a filter once it differs from the default, which already
		// hides a type of its own.
		isFiltered() {
			const defaults = getDefaultHiddenMessageTypes(this.mode);
			return this.hidden.length != defaults.length || this.hidden.some(t => !defaults.includes(t));
		},
		filterTitle() {
			if(!this.isFiltered) return 'Filter by message type';
			const shown = this.allTypes.filter(t => !this.hidden.includes(t)).length;
			return `Filter by message type (showing ${shown} of ${this.allTypes.length})`;
		},
	},
	async mounted() {
		try {
			this.loading++;
			this.eventTypes = await this.$api.get('userEventTypes') || [];
		} catch(e) {
			console.error(e);
		} finally {
			this.loading--;
		}
	},
	methods: {
		// The log panels clip their overflow, so a menu taller (or wider) than the panel would
		// simply be cut off. Measure what the nearest clipping ancestor allows and cap it there,
		// letting the menu scroll instead. Runs on `show` rather than `shown` because popper
		// places the menu from its size — capping it afterwards would leave it mispositioned
		// until the next open, when last time's cap is already applied.
		fitMenu() {
			const menu = this.$el.querySelector('.dropdown-menu');
			const toggle = this.$el.querySelector('.dropdown-toggle');
			if(!menu || !toggle) return;
			let clip = this.$el.parentElement;
			while(clip && clip !== document.body && getComputedStyle(clip).overflow === 'visible')
				clip = clip.parentElement;
			const bounds = (clip && clip !== document.body)
				? clip.getBoundingClientRect()
				: { top: 0, bottom: window.innerHeight, left: 0, right: window.innerWidth };
			const rect = toggle.getBoundingClientRect();
			const space = this.dropup ? rect.top - bounds.top : bounds.bottom - rect.bottom;
			menu.style.maxHeight = `${Math.max(140, space - 12)}px`;
			// 32rem is the two-column menu's natural cap; never grow past it, only shrink.
			menu.style.maxWidth = `${Math.max(200, Math.min(512, bounds.right - bounds.left - 12))}px`;
		},
		typeLabel(t) { return TYPE_LABELS[t.event_type] || t.event_name || t.event_type; },
		setTypeVisible(eventType, visible) {
			this.emitHidden(visible ? this.hidden.filter(t => t != eventType) : [ ...this.hidden, eventType ]);
		},
		showAllTypes() { this.emitHidden([]); },
		showNoTypes() { this.emitHidden(this.allTypes); },
		resetTypes() { this.emitHidden(getDefaultHiddenMessageTypes(this.mode)); },
		emitHidden(hidden) {
			saveHiddenMessageTypes(this.mode, hidden);
			this.$emit('input', hidden);
		},
	},
};
</script>

<style lang="scss">
@import '../plugins/variable.scss';
@import 'bootstrap/scss/functions';
@import 'bootstrap/scss/variables';

.message-type-filter > .btn.message-type-filter-btn {
	text-decoration: none;
	box-shadow: none;
}
.message-type-filter > .btn-link.message-type-filter-btn {
	color: inherit;
	opacity: .6;
	padding: 0 .3rem;
}
.message-type-filter > .btn-link.message-type-filter-btn:hover,
.message-type-filter.show > .btn-link.message-type-filter-btn {
	opacity: 1;
}
.message-type-filter-menu {
	// The dropdown background stays light while the app's body text is white.
	color: $dropdown-link-color;
	// Wide enough for the groups to sit side by side rather than wrap into one tall column.
	width: max-content;
	max-width: min(90vw, 32rem);
	max-height: 60vh;
	overflow-y: auto;

	.b-dropdown-form {
		padding: .5rem 1rem;
	}
	// One column per group, so the whole list stays short enough for a half-height panel.
	.message-type-filter-groups {
		display: flex;
		flex-wrap: wrap;
		align-items: flex-start;
		gap: .25rem 1.5rem;
	}
	.message-type-filter-group {
		min-width: 10rem;
	}
	.message-type-filter-group-title {
		font-size: .75rem;
		text-transform: uppercase;
		opacity: .6;
		margin-bottom: .2rem;
	}
	.message-type-filter-row {
		display: flex;
		align-items: center;
		white-space: nowrap;
	}
	.message-type-filter-gifts {
		margin-top: .6rem;
		padding-top: .5rem;
		border-top: 1px solid rgba(0, 0, 0, .1);
	}
	.custom-checkbox {
		white-space: nowrap;
	}
	// The theme's link green is unreadable on the light dropdown background.
	.btn-link.message-type-filter-action,
	.btn-link.message-type-filter-test {
		color: $dropdown-link-color;

		// $dropdown-link-hover-color is white in this theme, so underline instead of recoloring.
		&:hover, &:focus {
			color: $dropdown-link-color;
			text-decoration: underline;
		}
		&.disabled, &:disabled {
			color: $dropdown-link-color;
			opacity: .4;
		}
	}
	.btn-link.message-type-filter-test {
		font-size: .8rem;
	}
}
</style>
